import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from './context/ThemeContext';

/*
  ── IMAGE FILES REQUIRED ──────────────────────────────────
  Place both files in:  D:/Portfolio/public/
    public/bg-network.jpg   (dark mode — dark network/node image)
    public/bg-light.jpg     (light mode — colorful polygon triangles)
  ─────────────────────────────────────────────────────────
*/

const CSS = `
  .sbg-root {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
  }

  /* ── shared image div ── */
  .sbg-img {
    position: absolute;
    inset: -8%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transform-origin: center;
    will-change: transform;
  }

  /* ══ DARK MODE ══════════════════════════════════════════ */
  .sbg-dark .sbg-img {
    opacity: 0.72;
    mix-blend-mode: normal;
    filter: brightness(0.8) contrast(1.08) saturate(1.04);
    animation:
      sbg-drift   28s ease-in-out infinite alternate,
      sbg-breathe  9s ease-in-out infinite alternate;
  }
  .sbg-dark .sbg-glow {
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 40% 55% at 15% 20%, rgba(0,180,255,.10) 0%, transparent 70%),
      radial-gradient(ellipse 30% 40% at 82% 72%, rgba(0,229,255,.08) 0%, transparent 65%),
      radial-gradient(ellipse 20% 28% at 55% 88%, rgba(65,105,255,.06) 0%, transparent 60%);
    mix-blend-mode: screen;
    opacity: 0.82;
    animation: sbg-glow 22s ease-in-out infinite alternate;
  }
  .sbg-dark .sbg-sparkle {
    position: absolute; inset: 0;
    background-image:
      radial-gradient(circle, rgba(255,255,255,.75) 1px, transparent 1px),
      radial-gradient(circle, rgba(0,229,255,.55)   1px, transparent 1px),
      radial-gradient(circle, rgba(255,255,255,.45) 1px, transparent 1px);
    background-size: 220px 180px, 340px 260px, 180px 240px;
    background-position: 0 0, 110px 90px, 60px 150px;
    opacity: .35;
    animation: sbg-sparkle 14s ease-in-out infinite alternate;
  }

  /* ══ LIGHT MODE ═════════════════════════════════════════ */
  .sbg-light .sbg-img {
    opacity: 0.75;       /* strong enough to see the polygons */
    animation:
      sbg-drift-l   30s ease-in-out infinite alternate,
      sbg-breathe-l 12s ease-in-out infinite alternate;
  }
  /* frosted overlay — light enough that the image shows through */
  .sbg-light .sbg-frost {
    position: absolute; inset: 0;
    background: rgba(248, 250, 252, 0.42);   /* was .60 — reduced */
    animation: sbg-frost-pulse 12s ease-in-out infinite alternate;
  }
  .sbg-light .sbg-shimmer {
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 20% 30%, rgba(255,255,255,.22) 0%, transparent 65%),
      radial-gradient(ellipse 50% 35% at 78% 70%, rgba(255,200,80,.12)  0%, transparent 60%),
      radial-gradient(ellipse 40% 30% at 50% 10%, rgba(80,200,255,.10)  0%, transparent 55%);
    animation: sbg-shimmer 18s ease-in-out infinite alternate;
  }

  /* ══ KEYFRAMES — dark ═══════════════════════════════════ */
  @keyframes sbg-drift {
    0%  {transform:scale(1.04)translate(-1%,0%)}
    25% {transform:scale(1.06)translate(.5%,-.8%)}
    50% {transform:scale(1.05)translate(1%,.5%)}
    75% {transform:scale(1.07)translate(-.5%,1%)}
    100%{transform:scale(1.04)translate(0%,-.5%)}
  }
  @keyframes sbg-breathe {
    0%{opacity:.36} 50%{opacity:.48} 100%{opacity:.40}
  }
  @keyframes sbg-glow {
    0%  {opacity:.6;transform:translate(0px,0px)}
    33% {opacity:1;transform:translate(20px,-18px)}
    66% {opacity:.7;transform:translate(-15px,12px)}
    100%{opacity:.9;transform:translate(10px,-8px)}
  }
  @keyframes sbg-sparkle {
    0%  {opacity:.28;transform:translate(0px,0px)}
    50% {opacity:.46;transform:translate(8px,-6px)}
    100%{opacity:.32;transform:translate(-5px,4px)}
  }

  /* ══ KEYFRAMES — light ══════════════════════════════════ */
  @keyframes sbg-drift-l {
    0%  {transform:scale(1.00)translate(0%,0%)}
    25% {transform:scale(1.04)translate(.5%,-.6%)}
    50% {transform:scale(1.06)translate(1%,.4%)}
    75% {transform:scale(1.03)translate(-.4%,.8%)}
    100%{transform:scale(1.02)translate(.2%,-.3%)}
  }
  @keyframes sbg-breathe-l {
    0%{opacity:.70} 50%{opacity:.80} 100%{opacity:.74}
  }
  @keyframes sbg-frost-pulse {
    0%  {background:rgba(248,250,252,.44)}
    50% {background:rgba(248,250,252,.38)}
    100%{background:rgba(248,250,252,.46)}
  }
  @keyframes sbg-shimmer {
    0%  {opacity:.7;transform:translate(0px,0px)}
    33% {opacity:1;transform:translate(24px,-18px)}
    66% {opacity:.8;transform:translate(-16px,14px)}
    100%{opacity:.9;transform:translate(12px,-8px)}
  }
`;

let cssInjected = false;
function ensureCSS() {
  if (cssInjected || document.getElementById('sbg-css')) return;
  const el = document.createElement('style');
  el.id = 'sbg-css';
  el.textContent = CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

export default function SiteBackground() {
  const { isDark } = useTheme();
  const imgRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    ensureCSS();
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!imgRef.current) return;
    /* Served from /public — works without Vite import */
    const src = isDark ? '/bg-network.jpg' : '/bg-light.jpg';
    imgRef.current.style.backgroundImage = `url('${src}')`;
  }, [isDark]);

  if (!mounted) return null;

  return createPortal(
    <div className={`sbg-root ${isDark ? 'sbg-dark' : 'sbg-light'}`}>
      <div ref={imgRef} className="sbg-img" />
      {isDark ? (
        <>
          <div className="sbg-glow" />
          <div className="sbg-sparkle" />
        </>
      ) : (
        <>
          <div className="sbg-frost" />
          <div className="sbg-shimmer" />
        </>
      )}
    </div>,
    document.body
  );
}
